import React, { useEffect, useRef, useState } from "react";
import { nanoid } from "@reduxjs/toolkit";
import { isEmpty } from "lodash";
import TContactForm from "./type";
import { metaDataLanguageVersion } from "@/utilities/LanguageVersionSelectorShortcut";
import { useOutletContext } from "react-router-dom";
import TOutletContext from "@/cms/types/TOutletContext";
import useContactUs from "@/hooks/useContactUs";
import useMessage from "@/hooks/useMessage";
import RMDRTextBox from "../RMDRTextBox";
import RMDRTextArea from "../RMDRTextArea";


const unqKey = nanoid(12);

const Render = ( props:any ) => {

    const [disabled, setDisabled] = useState(false);

    const contactUs = useContactUs({ unqKey: unqKey });
    const message = useMessage({ unqKey: unqKey });

    const nameSurnameRef = useRef<any>(null);
    const emailRef = useRef<any>(null);
    const messageRef = useRef<any>(null);
    const titleRef = useRef<any>(null);

    useEffect(
        () => {
            if ( contactUs?.payload?.id && !isEmpty(contactUs?.payload?.id) ) {
                nameSurnameRef.current.value = "";
                emailRef.current.value = "";
                messageRef.current.value = "";
                titleRef.current.value = "";

                contactUs._remove();
                setDisabled(false);
                message._showMessage(props.messageHasBeenSentString);
            }
        }, [ contactUs?.payload ]
    )

    useEffect(
        () => {
            if ( !isEmpty(contactUs?.error) ) {
                setDisabled(false);
            }
        }, [ contactUs?.error ]
    )

    const onSubmit = ( event:React.SyntheticEvent ) => { 
        event.preventDefault();
        setDisabled(true);

        const target = event.target as typeof event.target & {
            nameSurname: { value: string },
            title: { value: string },
            email: { value: string },
            message: { value: string },
        };

        const _message = [];
        if ( props.allowSendURL ) {
            _message.push(`URL: ${window.location.href}`);
        }

        if ( _message.length > 0 ) {
            _message.push(`Message:\n ${target.message.value}`);
        }
        else {
            _message.push(target.message.value);
        }

        const payload:any = {
            nameSurname: target.nameSurname.value,
            email: target.email.value,
            message: _message.length > 1 ? _message.join("\n") : _message[0],
            title: target.title.value,
        }

        contactUs._post(payload);
    }

    return (
        <form method="post" className="contact-validation-active" id="contact-form-main" onSubmit={onSubmit}>
            <div className="container">
                <div className="contact-form-box">
                    <div className="row">
                        <div className="col-lg-6">
                            <RMDRTextBox
                                rmdrRef={nameSurnameRef}
                                id="nameSurname"
                                name="nameSurname"
                                placeholder={props.formNameSurnamePlaceholder}
                                disabled={disabled}
                                errors={contactUs?.error} 
                            />
                        </div>
                        <div className="col-lg-6">
                            <RMDRTextBox
                                rmdrRef={emailRef}
                                id="email"
                                name="email"
                                placeholder={props.formEmailPlaceholder}
                                disabled={disabled}
                                errors={contactUs?.error} 
                            />
                        </div>
                        <div className="col-lg-12 col-md-12">
                            <RMDRTextBox
                                rmdrRef={titleRef}
                                id="title"
                                name="title"
                                placeholder={props.formSubjectPlaceholder}
                                value={props.formDefaultSubject}
                                disabled={disabled}
                                className="form-group fullwidth"
                                errors={contactUs?.error} 
                            />
                        </div>
                        <div className="col-lg-12 col-md-12">
                            <RMDRTextArea 
                                rmdrRef={messageRef} 
                                name="message" 
                                id="message"
                                placeholder={props.formMessagePlaceholder} 
                                value={props.formDefaultMessage}
                                disabled={disabled} 
                                rows={5} 
                                errors={contactUs?.error} 
                                className="form-group fullwidth"
                            />
                        </div>
                        <div className="col-lg-12 col-12">
                            <div className="submit-area">
                                <button type="submit" className="btn btn-primary" disabled={disabled}>
                                    {
                                        disabled ? props.formSubmitButtonSendingString : props.formSubmitButtonString
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    )
}

const ContactForm = ( props: TContactForm ) => {
    console.log("ContactForm.rendered: ", props);

    const { siteSettingPayload } = useOutletContext<TOutletContext>();

    const formNameSurnamePlaceholder = metaDataLanguageVersion("formNameSurnamePlaceholder", siteSettingPayload.languageVersions);
    const formEmailPlaceholder = metaDataLanguageVersion("formEmailPlaceholder", siteSettingPayload.languageVersions);
    const formSubjectPlaceholder = metaDataLanguageVersion("formSubjectPlaceholder", siteSettingPayload.languageVersions);
    const formMessagePlaceholder = metaDataLanguageVersion("formMessagePlaceholder", siteSettingPayload.languageVersions);

    const formSubmitButtonString = metaDataLanguageVersion("formSubmitButtonString", siteSettingPayload.languageVersions);
    const formSubmitButtonSendingString = metaDataLanguageVersion("formSubmitButtonSendingString", siteSettingPayload.languageVersions);

    const formDefaultSubject = metaDataLanguageVersion("formDefaultSubject", siteSettingPayload.languageVersions);
    const formDefaultMessage = metaDataLanguageVersion("formDefaultMessage", siteSettingPayload.languageVersions);
    const messageHasBeenSentString = metaDataLanguageVersion("messageHasBeenSentString", siteSettingPayload.languageVersions);
    
    return (
        <Render 
            allowSendURL={props.allowSendURL}

            formNameSurnamePlaceholder={formNameSurnamePlaceholder}
            formEmailPlaceholder={formEmailPlaceholder}
            formSubjectPlaceholder={formSubjectPlaceholder}
            formMessagePlaceholder={formMessagePlaceholder}

            formSubmitButtonString={formSubmitButtonString}
            formSubmitButtonSendingString={formSubmitButtonSendingString}

            formDefaultSubject={formDefaultSubject}
            formDefaultMessage={formDefaultMessage}
            messageHasBeenSentString={messageHasBeenSentString}
        />
    )
}

const Component = React.memo(ContactForm, ( prevProps:TContactForm, nextProps:TContactForm ) => {
    console.log("ContactForm.memo", { prevProps, nextProps });
    return true;
});

export default Component;
